import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showSuccessToast } from "@stadium/services/react-toastify";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { batchPopulateOrder } from "@actions/order";
import { getCartOrderCurrency, getCartOrderId } from "@store/selectors/cart";
import { getStoreCurrencyCode } from "@store/selectors/store";
import { convertPrice } from "@helpers/currency";
import { getConversionRates } from "@stadium/redux-utils/selectors/configSelector";

import useFindOrCreateDraftOrder from "@hooks/cart/use-find-or-create-draft-order";
import { updateCart } from "@slices/cart";

import {
  ORDER_TYPES,
  itemRemovedSuccessMessage,
  itemUpdatedSuccessMessage,
} from "@constants/orders/cart";
import { ITEM_UPDATE_SUCCESS_TOAST_ID, ITEM_REMOVE_SUCCESS_TOAST_ID } from "@constants/toast-ids";

const useBatchPopulateItems = ({ type = ORDER_TYPES.lead } = {}) => {
  const dispatch = useDispatch();
  const currencyRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { findOrCreateDraftOrder, loading: fetchingDraftOrder } = useFindOrCreateDraftOrder({
    type,
  });
  const orderId = useSelector(getCartOrderId);
  const orderCurrencyCode = useSelector(getCartOrderCurrency);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);

  currencyRef.current = orderCurrencyCode || storeCurrencyCode;

  const conversionRates = useConfig(getConversionRates);

  const getPrice = ({ price, storePrice }) => {
    return storePrice
      ? convertPrice({
          value: storePrice,
          from: storeCurrencyCode,
          to: currencyRef.current,
          conversionRates,
        })
      : price;
  };

  const postPopulateOrder = useCallback(
    (order) => {
      dispatch(updateCart(order));
      setLoading(false);

      return order;
    },
    [dispatch, setLoading],
  );

  const sendPopulateCartRequest = useCallback(
    (orderId, payload) => {
      setLoading(true);
      return dispatch(batchPopulateOrder(orderId, payload))
        .then(postPopulateOrder)
        .catch((err) => {
          setLoading(false);
          throw err;
        });
    },
    [dispatch, postPopulateOrder],
  );

  const getUpdatedPayload = (payload) => {
    return {
      currency_code: currencyRef.current,
      line_items_attributes: payload.map((attributes) => ({
        ...attributes,
        price: getPrice({ price: attributes.price, storePrice: attributes.storePrice }),
      })),
    };
  };

  const sendRequest = useCallback(
    (payload) => {
      const orderPresentInCart = orderId;
      if (orderPresentInCart) return sendPopulateCartRequest(orderId, getUpdatedPayload(payload));
      return findOrCreateDraftOrder().then((order) => {
        currencyRef.current = order.currency;
        sendPopulateCartRequest(order.id, getUpdatedPayload(payload));
      });
    },
    [orderId, findOrCreateDraftOrder, sendPopulateCartRequest],
  );

  const addItemInCart = useCallback(sendRequest, [sendRequest]);

  const deleteItemFromCart = useCallback(
    (payload, onCompletion, showToast = true) =>
      sendRequest(payload).then(() => {
        onCompletion?.();
        if (showToast) {
          showSuccessToast({
            message: itemRemovedSuccessMessage,
            config: { toastId: ITEM_REMOVE_SUCCESS_TOAST_ID },
          });
        }
      }),
    [sendRequest],
  );

  const updateItemInCart = useCallback(
    (payload) =>
      sendRequest(payload).then(() =>
        showSuccessToast({
          message: itemUpdatedSuccessMessage,
          config: { toastId: ITEM_UPDATE_SUCCESS_TOAST_ID },
        }),
      )[sendRequest],
  );

  return {
    loading: loading || fetchingDraftOrder,
    addItemInCart,
    deleteItemFromCart,
    updateItemInCart,
  };
};

export default useBatchPopulateItems;
